// apps/admin/src/pages/GrowthConsolePage.tsx
//
// VALUTTEN Growth founder console (wave 2 S2-D.2). It calls the founder-only
// `founderListGrowthSignals` callable (services/admin-functions) and renders,
// per company at growth = 'propose', signal counts by detector, open and
// expired counts, the acted-on figure and the newest ok run per collector,
// plus the cross-company "growth rows without the flag" count (a rollback
// trigger). Tenants decide signals in their own inbox (OD2-2).
//
// Wave 4 (S4-C.8): each company row adds month-to-date agent cost (integer
// US cents, shown once / 100, and percent of cap), blocked attempts,
// draft_exhausted signals and verdict counts. Below the table: high-tier
// proposals with the founder's Approve and Dismiss (reason required), both
// through `founderDecideGrowthProposal`, and approved `schema` /
// `redirect_fix` exact changes for manual application.
//
// Wave 3 (S3-C.5): the conversion uploads panel, per collecting company with
// a platform enabled: keys by latest outcome (7 days), skip reasons, the last
// result code and the 28-day match rate or `insufficient_volume`. Counts and
// codes only: the payload carries no lead id or identifier value.
//
// Deep link: GROWTH_ADMIN_CONSOLE_URL (admin base + '/growth').

import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { AlertTriangle, CheckCircle2, Lightbulb, ShieldCheck, Wrench } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { PageHeader } from '@valutten/ui-common';
import { functions } from '../firebaseConfig';

export interface GrowthConsoleCompany {
  companyId: string;
  companyName: string;
  byDetector: Record<string, number>;
  open: number;
  expired: number;
  actedOn: { numerator: number; denominator: number; display: number | 'n/a' };
  lastOkRuns: Array<{ collector: string; finishedAt: string }>;
  agents: {
    monthKey: string;
    costCents: number;
    capCents: number;
    percentOfCap: number | null;
    blocked: number;
    draftExhausted: number;
  };
  verdicts: Record<string, number>;
  highTierPending: GrowthConsoleProposal[];
  approvedManual: GrowthConsoleProposal[];
}

export interface GrowthConsoleProposal {
  proposalId: string;
  signalId: string;
  kind: string;
  riskTier: string;
  title: string;
  exactChange: Record<string, unknown>;
  state: string;
  createdAt: string;
}

export type ConversionKeyState = 'pending' | 'validated' | 'ingested' | 'accepted' | 'rejected' | 'error' | 'skipped';

export interface ConversionsPlatformPanel {
  platform: 'google' | 'meta';
  keysByLatestOutcome: Record<ConversionKeyState, number>;
  skipReasons: Record<string, number>;
  lastResultCode: string | null;
  matchRate: {
    state: 'ok' | 'insufficient_volume';
    finalOutcomes: number;
    accepted: number;
    acceptedPercent: number | null;
    byIdentifiers: Array<{ identifiers: string; finalOutcomes: number; accepted: number; acceptedPercent: number }>;
  };
}

export interface GrowthConsoleConversions {
  windowDays: number;
  matchRateDays: number;
  minFinalOutcomes: number;
  companies: Array<
    | { companyId: string; companyName: string; state: 'ok'; platforms: ConversionsPlatformPanel[] }
    | { companyId: string; companyName: string; state: 'unavailable' }
  >;
}

export interface GrowthConsoleData {
  companies: GrowthConsoleCompany[];
  noFlagGrowthRows: number;
  openSignalDays: number;
  noFlagWindowHours: number;
  /** Signals read and "expired" counted within this many days (58, as the digest). */
  expiredWindowDays: number;
  conversions: GrowthConsoleConversions;
}

const KEY_STATES: ConversionKeyState[] = ['pending', 'validated', 'ingested', 'accepted', 'rejected', 'error', 'skipped'];
const PLATFORM_LABEL: Record<ConversionsPlatformPanel['platform'], string> = { google: 'Google', meta: 'Meta' };

function formatBrisbane(iso: string): string {
  return new Intl.DateTimeFormat('en-AU', {
    timeZone: 'Australia/Brisbane',
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(new Date(iso));
}

const humanise = (s: string): string => s.replace(/_/g, ' ');

/** Integer US cents to dollars: the one / 100, at presentation. */
export const formatUsCents = (cents: number): string =>
  (cents / 100).toLocaleString('en-AU', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 });

const VERDICT_ORDER = ['win', 'loss', 'inconclusive', 'not_judgeable', 'not_applied'];

const ExactChange: React.FC<{ change: Record<string, unknown> }> = ({ change }) => (
  <dl className="grid grid-cols-1 sm:grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-xs">
    {Object.entries(change)
      .filter(([k]) => k !== 'kind')
      .map(([k, v]) => (
        <React.Fragment key={k}>
          <dt className="text-slate-400">{humanise(k)}</dt>
          <dd className="font-mono text-slate-200 break-all whitespace-pre-wrap">
            {/* W4-D27: a null campaign is the ad account grain. */}
            {v === null ? (k === 'campaignId' ? 'ad account' : 'n/a') : Array.isArray(v) ? v.join(', ') : String(v)}
          </dd>
        </React.Fragment>
      ))}
  </dl>
);

const GrowthConsolePage: React.FC = () => {
  const [data, setData] = useState<GrowthConsoleData | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [dismissing, setDismissing] = useState<{ companyId: string; proposalId: string } | null>(null);
  const [reason, setReason] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await httpsCallable<Record<string, never>, GrowthConsoleData>(functions, 'founderListGrowthSignals')({});
      setData(res.data);
    } catch (err) {
      console.error('[GrowthConsolePage] founderListGrowthSignals failed:', err);
      toast.error('Could not load the Growth console');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const decide = useCallback(
    async (companyId: string, proposalId: string, action: 'approve' | 'dismiss', why?: string) => {
      setBusy(proposalId);
      try {
        const res = await httpsCallable<
          { companyId: string; proposalId: string; action: 'approve' | 'dismiss'; reason?: string },
          { status: 'recorded' | 'aborted'; state: string }
        >(functions, 'founderDecideGrowthProposal')(why ? { companyId, proposalId, action, reason: why } : { companyId, proposalId, action });
        if (res.data.status === 'recorded') toast.success(`Proposal ${res.data.state}`);
        else toast.error(`Not recorded: the proposal is ${res.data.state}`);
        setDismissing(null);
        setReason('');
        await load();
      } catch (err) {
        console.error('[GrowthConsolePage] founderDecideGrowthProposal failed:', err);
        toast.error(err instanceof Error ? err.message : 'The decision was not saved');
      } finally {
        setBusy(null);
      }
    },
    [load]
  );

  const detectors = data
    ? Array.from(new Set(data.companies.flatMap((c) => Object.keys(c.byDetector)))).sort()
    : [];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Growth Console"
        subtitle={`Companies at growth = 'propose': signals detected in the last ${
          data?.expiredWindowDays ?? 58
        } days, decisions, agent cost and collector health. Open means undecided within ${data?.openSignalDays ?? 30} days; expired means undecided and older, within the last ${
          data?.expiredWindowDays ?? 58
        } days. Only high-tier proposals take a founder decision.`}
        onRefresh={() => void load()}
        isRefreshing={loading}
        actions={<Lightbulb className="w-6 h-6 text-amber-400" aria-hidden="true" />}
      />

      {loading && !data ? (
        <div className="text-slate-400 text-sm">Loading...</div>
      ) : data ? (
        <>
          <div
            data-testid="growth-no-flag"
            className={`rounded-lg border p-4 text-sm flex items-center gap-2 ${
              data.noFlagGrowthRows > 0
                ? 'border-red-500/40 bg-red-500/10 text-red-200'
                : 'border-slate-700 bg-slate-800/50 text-slate-300'
            }`}
          >
            {data.noFlagGrowthRows > 0 ? (
              <AlertTriangle className="w-4 h-4 text-red-400" />
            ) : (
              <CheckCircle2 className="w-4 h-4 text-emerald-400" />
            )}
            Growth ledger rows for companies without the flag (last {data.noFlagWindowHours} hours):{' '}
            <span className="font-semibold">{data.noFlagGrowthRows.toLocaleString('en-AU')}</span>
            {data.noFlagGrowthRows > 0 && ' (rollback trigger, plan section 11)'}
          </div>

          {data.companies.length === 0 ? (
            <div className="text-slate-400 text-sm">No company is at growth = 'propose'.</div>
          ) : (
            <div className="rounded-lg border border-slate-700 bg-slate-800/50 overflow-x-auto">
              <table className="w-full text-sm" data-testid="growth-console-table">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-slate-400 border-b border-slate-700">
                    <th className="px-4 py-3 font-medium">Company</th>
                    {detectors.map((d) => (
                      <th key={d} className="px-4 py-3 font-medium text-right">
                        {humanise(d)} signals
                      </th>
                    ))}
                    <th className="px-4 py-3 font-medium text-right">Open</th>
                    <th className="px-4 py-3 font-medium text-right">Expired in the last {data.expiredWindowDays} days</th>
                    <th className="px-4 py-3 font-medium text-right">Acted on (30 d)</th>
                    <th className="px-4 py-3 font-medium text-right">Agent cost (month to date)</th>
                    <th className="px-4 py-3 font-medium text-right">Blocked</th>
                    <th className="px-4 py-3 font-medium text-right">Draft exhausted</th>
                    <th className="px-4 py-3 font-medium">Verdicts</th>
                    <th className="px-4 py-3 font-medium">Last ok run per collector</th>
                  </tr>
                </thead>
                <tbody>
                  {data.companies.map((c) => (
                    <tr key={c.companyId} className="border-b border-slate-700/60 last:border-b-0 align-top">
                      <td className="px-4 py-3">
                        <Link to={`/companies/${c.companyId}`} className="text-white font-semibold hover:underline">
                          {c.companyName}
                        </Link>
                      </td>
                      {detectors.map((d) => (
                        <td key={d} className="px-4 py-3 text-right text-slate-200">
                          {(c.byDetector[d] ?? 0).toLocaleString('en-AU')}
                        </td>
                      ))}
                      <td className="px-4 py-3 text-right text-slate-200">{c.open.toLocaleString('en-AU')}</td>
                      <td className="px-4 py-3 text-right text-slate-400">{c.expired.toLocaleString('en-AU')}</td>
                      <td className="px-4 py-3 text-right text-slate-200" title={`${c.actedOn.numerator} of ${c.actedOn.denominator}`}>
                        {c.actedOn.display === 'n/a' ? 'n/a' : `${c.actedOn.display}%`}
                      </td>
                      <td className="px-4 py-3 text-right text-slate-200" data-testid={`growth-cost-${c.companyId}`}>
                        {formatUsCents(c.agents.costCents)}
                        <span className="block text-xs text-slate-400">
                          {c.agents.percentOfCap === null ? 'no cap' : `${c.agents.percentOfCap}% of ${formatUsCents(c.agents.capCents)}`}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right text-slate-200" data-testid={`growth-blocked-${c.companyId}`}>
                        {c.agents.blocked.toLocaleString('en-AU')}
                      </td>
                      <td className="px-4 py-3 text-right text-slate-200" data-testid={`growth-exhausted-${c.companyId}`}>
                        {c.agents.draftExhausted.toLocaleString('en-AU')}
                      </td>
                      <td className="px-4 py-3 text-slate-300" data-testid={`growth-verdicts-${c.companyId}`}>
                        {Object.keys(c.verdicts).length === 0 ? (
                          <span className="text-slate-500">none</span>
                        ) : (
                          [...VERDICT_ORDER, ...Object.keys(c.verdicts).filter((v) => !VERDICT_ORDER.includes(v))]
                            .filter((v) => c.verdicts[v] !== undefined)
                            .map((v) => `${humanise(v)} ${c.verdicts[v]}`)
                            .join(', ')
                        )}
                      </td>
                      <td className="px-4 py-3 text-slate-300">
                        {c.lastOkRuns.length === 0 ? (
                          <span className="text-slate-500">none</span>
                        ) : (
                          <ul className="space-y-0.5">
                            {c.lastOkRuns.map((r) => (
                              <li key={r.collector}>
                                {humanise(r.collector)}: {formatBrisbane(r.finishedAt)}
                              </li>
                            ))}
                          </ul>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <section data-testid="growth-high-tier" className="rounded-lg border border-slate-700 bg-slate-800/50 p-4 space-y-3">
            <h3 className="text-sm font-semibold text-white flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-amber-400" />
              High-tier proposals awaiting VALUTTEN review
            </h3>
            {data.companies.every((c) => c.highTierPending.length === 0) ? (
              <p className="text-sm text-slate-400">None awaiting review.</p>
            ) : (
              <ul className="space-y-3">
                {data.companies.flatMap((c) =>
                  c.highTierPending.map((p) => (
                    <li key={p.proposalId} data-testid={`growth-pending-${p.proposalId}`} className="border-b border-slate-700/60 pb-3 last:border-b-0">
                      <div className="flex flex-wrap items-center gap-2 text-sm">
                        <span className="font-semibold text-white">{c.companyName}</span>
                        <span className="text-slate-300">{p.title}</span>
                        <span className="text-xs uppercase tracking-wide text-slate-400">{humanise(p.kind)}</span>
                      </div>
                      <ExactChange change={p.exactChange} />
                      <div className="mt-2 flex flex-wrap gap-2">
                        <button
                          type="button"
                          disabled={busy !== null}
                          onClick={() => void decide(c.companyId, p.proposalId, 'approve')}
                          className="rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-500 disabled:opacity-50"
                        >
                          Approve
                        </button>
                        <button
                          type="button"
                          disabled={busy !== null}
                          onClick={() => {
                            setDismissing({ companyId: c.companyId, proposalId: p.proposalId });
                            setReason('');
                          }}
                          className="rounded-md border border-slate-600 px-3 py-1.5 text-sm font-medium text-slate-200 hover:bg-slate-700/60 disabled:opacity-50"
                        >
                          Dismiss
                        </button>
                      </div>
                      {dismissing?.proposalId === p.proposalId && (
                        <form
                          className="mt-2 space-y-2"
                          onSubmit={(e) => {
                            e.preventDefault();
                            if (reason.trim() !== '') void decide(c.companyId, p.proposalId, 'dismiss', reason.trim());
                          }}
                        >
                          <label htmlFor={`dismiss-${p.proposalId}`} className="block text-xs text-slate-300">
                            Reason (required). No names, email addresses or phone numbers.
                          </label>
                          <textarea
                            id={`dismiss-${p.proposalId}`}
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            maxLength={500}
                            rows={2}
                            className="w-full rounded-md border border-slate-600 bg-slate-900/60 px-3 py-2 text-sm text-slate-100"
                          />
                          <button
                            type="submit"
                            disabled={reason.trim() === '' || busy !== null}
                            className="rounded-md bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-500 disabled:opacity-50"
                          >
                            Confirm dismiss
                          </button>
                        </form>
                      )}
                    </li>
                  ))
                )}
              </ul>
            )}
          </section>

          <section data-testid="growth-manual" className="rounded-lg border border-slate-700 bg-slate-800/50 p-4 space-y-3">
            <h3 className="text-sm font-semibold text-white flex items-center gap-2">
              <Wrench className="w-4 h-4 text-emerald-400" />
              Approved site changes to apply by hand
            </h3>
            {data.companies.every((c) => c.approvedManual.length === 0) ? (
              <p className="text-sm text-slate-400">Nothing approved to apply.</p>
            ) : (
              <ul className="space-y-3">
                {data.companies.flatMap((c) =>
                  c.approvedManual.map((p) => (
                    <li key={p.proposalId} data-testid={`growth-manual-${p.proposalId}`} className="border-b border-slate-700/60 pb-3 last:border-b-0">
                      <div className="flex flex-wrap items-center gap-2 text-sm">
                        <span className="font-semibold text-white">{c.companyName}</span>
                        <span className="text-slate-300">{p.title}</span>
                        <span className="text-xs uppercase tracking-wide text-slate-400">{humanise(p.kind)}</span>
                      </div>
                      <ExactChange change={p.exactChange} />
                    </li>
                  ))
                )}
              </ul>
            )}
          </section>

          {data.conversions && (
            <section className="space-y-3" data-testid="growth-conversions">
              <h2 className="text-lg font-semibold text-white">Conversion uploads</h2>
              <p className="text-slate-400 text-sm">
                Keys by latest outcome per key, last {data.conversions.windowDays} days. Match rate: accepted share of final
                outcomes (accepted or rejected) by identifiers sent, last {data.conversions.matchRateDays} days; insufficient
                volume below {data.conversions.minFinalOutcomes} final outcomes.
              </p>
              {data.conversions.companies.length === 0 ? (
                <div className="text-slate-400 text-sm">No collecting company has a conversion platform enabled.</div>
              ) : (
                <div className="rounded-lg border border-slate-700 bg-slate-800/50 overflow-x-auto">
                  <table className="w-full text-sm" data-testid="growth-conversions-table">
                    <thead>
                      <tr className="text-left text-xs uppercase tracking-wide text-slate-400 border-b border-slate-700">
                        <th className="px-4 py-3 font-medium">Company</th>
                        <th className="px-4 py-3 font-medium">Platform</th>
                        {KEY_STATES.map((s) => (
                          <th key={s} className="px-4 py-3 font-medium text-right">
                            {s}
                          </th>
                        ))}
                        <th className="px-4 py-3 font-medium">Skip reasons</th>
                        <th className="px-4 py-3 font-medium">Last result code</th>
                        <th className="px-4 py-3 font-medium">Match rate ({data.conversions.matchRateDays} d)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.conversions.companies.flatMap((c) =>
                        c.state === 'unavailable' ? (
                          <tr key={c.companyId} className="border-b border-slate-700/60 last:border-b-0 align-top">
                            <td className="px-4 py-3">
                              <Link to={`/companies/${c.companyId}`} className="text-white font-semibold hover:underline">
                                {c.companyName}
                              </Link>
                            </td>
                            <td colSpan={KEY_STATES.length + 4} className="px-4 py-3 text-amber-300">
                              Conversion data unavailable: the ledger read failed. Refresh to retry.
                            </td>
                          </tr>
                        ) : c.platforms.map((p) => {
                          const reasons = Object.entries(p.skipReasons).sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
                          return (
                            <tr
                              key={`${c.companyId}-${p.platform}`}
                              className="border-b border-slate-700/60 last:border-b-0 align-top"
                            >
                              <td className="px-4 py-3">
                                <Link to={`/companies/${c.companyId}`} className="text-white font-semibold hover:underline">
                                  {c.companyName}
                                </Link>
                              </td>
                              <td className="px-4 py-3 text-slate-200">{PLATFORM_LABEL[p.platform]}</td>
                              {KEY_STATES.map((s) => (
                                <td key={s} className="px-4 py-3 text-right text-slate-200">
                                  {(p.keysByLatestOutcome[s] ?? 0).toLocaleString('en-AU')}
                                </td>
                              ))}
                              <td className="px-4 py-3 text-slate-300">
                                {reasons.length === 0 ? (
                                  <span className="text-slate-500">none</span>
                                ) : (
                                  <ul className="space-y-0.5">
                                    {reasons.map(([reason, n]) => (
                                      <li key={reason}>
                                        {humanise(reason)}: {n.toLocaleString('en-AU')}
                                      </li>
                                    ))}
                                  </ul>
                                )}
                              </td>
                              <td className="px-4 py-3 text-slate-300">
                                {p.lastResultCode ?? <span className="text-slate-500">none</span>}
                              </td>
                              <td className="px-4 py-3 text-slate-300">
                                {p.matchRate.state === 'insufficient_volume' ? (
                                  <span className="text-amber-300">
                                    Insufficient volume ({p.matchRate.finalOutcomes} of {data.conversions.minFinalOutcomes} final
                                    outcomes)
                                  </span>
                                ) : (
                                  <>
                                    <div className="text-slate-200">
                                      {p.matchRate.acceptedPercent}% ({p.matchRate.accepted} of {p.matchRate.finalOutcomes})
                                    </div>
                                    <ul className="space-y-0.5">
                                      {p.matchRate.byIdentifiers.map((g) => (
                                        <li key={g.identifiers}>
                                          {g.identifiers}: {g.acceptedPercent}% ({g.accepted} of {g.finalOutcomes})
                                        </li>
                                      ))}
                                    </ul>
                                  </>
                                )}
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          )}
        </>
      ) : null}
    </div>
  );
};

export default GrowthConsolePage;
